import React, { useEffect, useRef, useState } from 'react';
import { chargerGoogleMaps } from '../lib/googleMaps';
import './LocationPicker.css';

interface Props {
  /** [latitude, longitude], ou null si la position n'est pas encore connue. */
  value: [number, number] | null;
  onChange: (position: [number, number]) => void;
  /** Centre par défaut quand aucune position n'est posée. Lille. */
  centreParDefaut?: [number, number];
}

const LILLE: [number, number] = [50.62925, 3.057256];

/**
 * Carte avec marqueur déplaçable — le rattrapage indispensable à côté de la
 * recherche d'adresse.
 *
 * Places renvoie le point de l'adresse POSTALE, qui n'est pas toujours l'entrée
 * du logement ; et certaines adresses ne sont pas reconnues du tout. Sans ce
 * réglage manuel, un propriétaire dans ce cas n'aurait aucun moyen de poser sa
 * position — donc aucun moyen de publier, puisque la carte du site public est
 * affichée sans condition.
 */
const LocationPicker: React.FC<Props> = ({ value, onChange, centreParDefaut = LILLE }) => {
  const conteneur = useRef<HTMLDivElement>(null);
  const carte = useRef<any>(null);
  const marqueur = useRef<any>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  // Ref et non state : lue dans les gestionnaires d'événements de la carte,
  // qui ne sont attachés qu'une fois et captureraient une valeur figée.
  const rappel = useRef(onChange);
  rappel.current = onChange;

  useEffect(() => {
    let annule = false;

    chargerGoogleMaps()
      .then((maps) => {
        if (annule || !conteneur.current) return;
        const centre = value ?? centreParDefaut;

        carte.current = new maps.Map(conteneur.current, {
          center: { lat: centre[0], lng: centre[1] },
          // Zoom serré quand on sait où on est, large quand on cherche encore.
          zoom: value ? 17 : 12,
          // Boutons + / − : indispensables pour poser un marqueur au bon
          // endroit, l'adresse postale ne tombant pas toujours sur l'entrée
          // du logement.
          zoomControl: true,
          // « cooperative » : la molette seule fait défiler la PAGE, ctrl +
          // molette zoome. La carte est au milieu d'un long formulaire —
          // capturer la molette y piégerait le défilement.
          gestureHandling: 'cooperative',
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        });

        marqueur.current = new maps.Marker({
          map: carte.current,
          position: { lat: centre[0], lng: centre[1] },
          draggable: true,
        });

        marqueur.current.addListener('dragend', (e: any) => {
          rappel.current([e.latLng.lat(), e.latLng.lng()]);
        });
        // Cliquer pose aussi le marqueur : plus rapide que le glisser quand
        // il faut le déplacer loin.
        carte.current.addListener('click', (e: any) => {
          marqueur.current.setPosition(e.latLng);
          rappel.current([e.latLng.lat(), e.latLng.lng()]);
        });
      })
      .catch(() => {
        if (!annule) setErreur('Carte indisponible.');
      });

    return () => {
      annule = true;
    };
    // Montage unique : les changements de `value` sont répercutés par l'effet
    // ci-dessous, sans reconstruire la carte (ce qui perdrait le zoom).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recentrage quand la position change depuis l'extérieur (choix d'une
  // adresse dans la liste), sans toucher au zoom choisi par l'utilisateur.
  useEffect(() => {
    if (!value || !carte.current || !marqueur.current) return;
    const position = { lat: value[0], lng: value[1] };
    marqueur.current.setPosition(position);
    carte.current.panTo(position);
    if (carte.current.getZoom() < 15) carte.current.setZoom(17);
  }, [value]);

  if (erreur) {
    return <p className="carte-erreur">{erreur} Vous pouvez continuer sans elle.</p>;
  }

  return (
    <div className="carte-position">
      <div ref={conteneur} className="carte-position-toile" />
      <p className="carte-position-aide">
        {value
          ? 'Déplacez le marqueur, ou cliquez sur la carte, pour ajuster la position exacte du logement. Zoomez avec les boutons + et − pour viser précisément.'
          : 'Choisissez une adresse ci-dessus, ou cliquez directement sur la carte pour poser la position.'}
      </p>
    </div>
  );
};

export default LocationPicker;
